import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { projects } from '../data/portfolio';
import { Reveal } from './Motion';

const projectsById = Object.fromEntries(projects.map(project => [project.id, project]));

const EDITORIAL_EASE = [0.22, 1, 0.36, 1];
const CARD_HIDDEN = { opacity: 0, y: 12 };
const CARD_SHOWN = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, amount: 0.08 };
const STAGGER_STEP = 0.045;

export const ProjectCard = ({ project, number, image, instance = 'featured', detailed = false, onTitleEnter, onTitleMove, onTitleLeave }) => {
  const reduced = useReducedMotion();
  return <motion.a href={project.behanceUrl} target="_blank" rel="noopener noreferrer" className={`project-card project-${project.id} ${detailed ? 'project-detailed' : ''}`}
    initial={reduced ? false : CARD_HIDDEN} whileInView={CARD_SHOWN} viewport={CARD_VIEWPORT}
    transition={{ duration: 0.55, delay: reduced ? 0 : (Number(number || project.number) % 3) * STAGGER_STEP, ease: EDITORIAL_EASE }}
    data-project="true" data-testid={`${instance}-project-${project.id}`} aria-label={`${project.title}, ${project.profileLink ? 'visit Behance profile' : 'view case study on Behance'} (opens in a new tab)`}>
    <div className="project-visual">
      <img src={image || project.image} alt={`${project.title}, ${project.category}`} loading="lazy" data-testid={`${instance}-image-${project.id}`} />
      <span className="project-shade" />
      <div className="project-caption">
        <span className="project-number" data-testid={`${instance}-number-${project.id}`}>{number || project.number}</span>
        <h3 data-testid={`${instance}-title-${project.id}`}>{project.title}</h3>
        <p data-testid={`${instance}-category-${project.id}`}>{project.category}</p>
      </div>
      <span className="project-view" aria-hidden="true">VIEW PROJECT <ArrowUpRight size={14} /></span>
    </div>
    {detailed && <div className="project-details" data-testid={`archive-details-${project.id}`}
      onMouseEnter={onTitleEnter ? (e) => onTitleEnter(image || project.image, e) : undefined}
      onMouseMove={onTitleMove}
      onMouseLeave={onTitleLeave}>
      <div className="project-detail-top"><h3>{project.title}</h3><span>{project.year || 'YEAR TBC'}</span></div>
      <p className="archive-category">{project.category}</p>
      <p>{project.description}</p>
      <div className="project-detail-bottom"><span>{project.tools.length ? project.tools.join(' · ') : 'Tool details on Behance'}</span><span>{project.profileLink ? 'BEHANCE PROFILE' : 'CASE STUDY'} ↗</span></div>
    </div>}
  </motion.a>;
};

export const FeaturedWork = () => (
  <section id="work" className="featured-work" data-testid="featured-work" aria-labelledby="featured-title">
    <div className="work-mosaic">
      <div className="work-intro mosaic-tile" data-testid="work-intro-tile">
        <span className="eyebrow">01 / SELECTED WORK</span>
        <div><h2 id="featured-title" data-testid="featured-title">IDEAS IN<br />THE WILD.</h2>
          <p data-testid="featured-description">A mix of client work, collaborations and things I made because I couldn't not make them.</p>
          <Link to="/work" className="text-link" data-testid="view-all-work">VIEW ALL WORK <span>→</span></Link>
        </div>
      </div>
      <div className="mosaic-tile color-tile terracotta-tile" aria-hidden="true" />
      <ProjectCard project={projectsById.solara} />
      <ProjectCard project={projectsById.rarefore} />
      <ProjectCard project={projectsById.photobook} />
      <div className="mosaic-tile color-tile taupe-tile" aria-hidden="true" />
      <ProjectCard project={projectsById['kora-estate']} />
      <div className="mosaic-tile color-tile navy-tile" aria-hidden="true" />
      <ProjectCard project={projectsById['life-in-rarez']} />
      <div className="mosaic-tile color-tile khaki-tile" aria-hidden="true" />
      <ProjectCard project={projectsById.enchantoys} />
      <ProjectCard project={projectsById.nimmalakunta} />
    </div>
    <Reveal className="work-colophon"><span data-testid="work-note">A FEW THINGS I'VE PUT OUT INTO THE WORLD.</span><span data-testid="work-period">SELECTED WORK / 2025–2026</span></Reveal>
  </section>
);